import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { reduxForm, InjectedFormProps, formValueSelector } from "redux-form";
import { useLocation, useNavigate } from "react-router";

import { Button, SubmitBtn } from "capitalrx-components";
import { useLoosePermissionCheck } from "capitalrx-shell";
import { queryString } from "capitalrx-utilities";
import GeneralClaimInformation from "@/App/pages/TestClaim/GeneralInformation";
import TestClaimImageContainer from "@/App/components/TestClaimImageContainer";
import TestClaimSectionContainer from "@/App/components/TestClaimSectionContainer";
import { testAsBatch } from "@/App/constants/permissions";
import { ReactComponent as PatientSearchImage } from "@/images/patient-search.svg";
import { ReactComponent as PharmacySearchImage } from "@/images/pharmacy-search.svg";
import { ReactComponent as DrugSearchImage } from "@/images/drug-search.svg";
import PatientSummary from "./PatientSummary";
import PharmacySummary from "./PharmacySummary";
import DrugSummary from "./DrugSummary";
import { transmissionTestActions, transmissionTestAsBatchActions } from "@/App/actions/";
import { RootState } from "@/App/reducers";
import { TransmissionTypes } from "@/types";
import { logEvent, ANALYTIC_EVENTS } from "@/App/utils/analytics";
import { transformRepeatedField } from "@/App/utils/testClaimUtils";

type transmissionOptions = {
  [key: string]: string | number;
};

const formName = "TestClaim";

const TestClaimForm: React.FC<InjectedFormProps> = (props: InjectedFormProps) => {
  const { submitting, handleSubmit } = props;
  const { search } = useLocation();
  const { member_id, npi, drugs, date_of_service } = queryString.getQueryParams(search);
  const canProcessTestAsBatch = useLoosePermissionCheck(testAsBatch);

  const dispatch = useDispatch();
  const { submitTransmissionTest } = transmissionTestActions;
  const { submitTransmissionTestAsBatch } = transmissionTestAsBatchActions;

  const navigate = useNavigate();

  const handleAmplitudeLogging: Function = (amplitudeTag: string): void => {
    logEvent(amplitudeTag);
  };

  const parseFormData = (data: TransmissionTypes.TransmissionTestFormData): TransmissionTypes.TransmissionData => {
    let optionalParams = {};
    if (data.days_supply) optionalParams = { ...optionalParams, days_supply: parseInt(data.days_supply) };
    if (data.daw) optionalParams = { ...optionalParams, daw: data.daw };
    if (data.usual_and_customary_charge)
      optionalParams = { ...optionalParams, usual_and_customary_charge: data.usual_and_customary_charge };
    if (data.fill_number) optionalParams = { ...optionalParams, fill_number: parseInt(data.fill_number) };
    if (data.prescription_service_reference_number)
      optionalParams = { ...optionalParams, prescription_service_reference_number: data.prescription_service_reference_number };
    if (data.submission_clarification_code_1)
      optionalParams = { ...optionalParams, submission_clarification_code_1: data.submission_clarification_code_1 };
    if (data.diagnosis_code_qualifier_1)
      optionalParams = { ...optionalParams, diagnosis_code_qualifier_1: data.diagnosis_code_qualifier_1 };
    if (data.diagnosis_code_1)
      optionalParams = { ...optionalParams, diagnosis_code_1: data.diagnosis_code_1 };

    if (data.other_coverage_code) optionalParams = { ...optionalParams, other_coverage_code: data.other_coverage_code };
    if (data.other_payer_id_qualifier_1) optionalParams = { ...optionalParams, other_payer_id_qualifier_1: data.other_payer_id_qualifier_1 };
    if (data.other_payer_id_1) optionalParams = { ...optionalParams, other_payer_id_1: data.other_payer_id_1 };

    if (data.other_payer_amount_paid_1) {
      optionalParams = {
        ...optionalParams,
        other_payer_amount_paid_1: data.other_payer_amount_paid_1,
        other_payer_amount_paid_qualifier_1: "07",
      };
    }
    if (data.other_payer_patient_responsibility_amount_1) {
      optionalParams = {
        ...optionalParams,
        other_payer_patient_responsibility_amount_1: data.other_payer_patient_responsibility_amount_1,
        other_payer_patient_responsibility_amount_qualifier_1: "06",
      };
    }
    if (data.other_payer_reject_code_1) optionalParams = { ...optionalParams, other_payer_reject_code_1: data.other_payer_reject_code_1 };

    if (data.special_packaging_indicator) optionalParams = { ...optionalParams, special_packaging_indicator: data.special_packaging_indicator };

    if (data.professional_service_code) {
      const professionalServiceCodes = transformRepeatedField(data.professional_service_code, "professional_service_code");
      optionalParams = {
        ...optionalParams,
        ...professionalServiceCodes,
      };
    }

    if (data.dur_pps_level_of_effort) {
      const levelOfEfforts = transformRepeatedField(data.dur_pps_level_of_effort, "dur_pps_level_of_effort");
      optionalParams = {
        ...optionalParams,
        ...levelOfEfforts,
      };
    }

    if (data.reason_for_service_code) {
      const reasonForServiceCodes = transformRepeatedField(data.reason_for_service_code, "reason_for_service_code");
      optionalParams = {
        ...optionalParams,
        ...reasonForServiceCodes,
      };
    }

    if (data.result_of_service_code) {
      const resultOfServiceCodes = transformRepeatedField(data.result_of_service_code, "result_of_service_code");
      optionalParams = {
        ...optionalParams,
        ...resultOfServiceCodes,
      };
    }

    if (data.ingredient_cost_submitted) {
      optionalParams = {
        ...optionalParams,
        ingredient_cost_submitted: data.ingredient_cost_submitted,
      };
    }

    if (data.dispensing_fee_submitted) {
      optionalParams = {
        ...optionalParams,
        dispensing_fee_submitted: data.dispensing_fee_submitted,
      };
    }

    if (data.incentive_amount_submitted) {
      optionalParams = {
        ...optionalParams,
        incentive_amount_submitted: data.incentive_amount_submitted,
      };
    }

    if (data.prescriber_id_qualifier) optionalParams = { ...optionalParams, prescriber_id_qualifier: data.prescriber_id_qualifier };
    if (data.prescriber_id) optionalParams = { ...optionalParams, prescriber_id: data.prescriber_id };
    if (data.pharmacy_service_type) optionalParams = { ...optionalParams, pharmacy_service_type: data.pharmacy_service_type };
    if (data.place_of_service) optionalParams = { ...optionalParams, place_of_service: data.place_of_service };
    if (data.patient_residence) optionalParams = { ...optionalParams, patient_residence: data.patient_residence };
    if (data.prescription_origin_code) optionalParams = { ...optionalParams, prescription_origin_code: data.prescription_origin_code };

    const processor_control_number = data.plan?.processor_control_number?.split(",")[0]?.trim() || "";

    let patient_gender_code = "0";
    if (data.details.sex === "M") {
      patient_gender_code = "1";
    } else if (data.details.sex === "F") {
      patient_gender_code = "2";
    }

    let transmissionData = {
      bin_number: data.plan.bin_number,
      processor_control_number: processor_control_number,
      service_provider_id: data.pharmacy.details.npi,
      service_provider_id_qualifier: "01",
      date_of_service: data.date_of_service,
      submitted_cardholder_id: data.details.external_member_id,
      group_id: data.plan.group_number,
      external_group_id: data.group.external_group_id,
      person_code: data.details.person_code,
      date_of_birth: data.details.birth_date,
      patient_gender_code: patient_gender_code,
      patient_first_name: data.details.first_name,
      patient_last_name: data.details.last_name,
      member_id: data.details.id && data.details.id.toString(),
      client_id: data.plan.client_id,
      ...optionalParams,
    } as TransmissionTypes.TransmissionData;

    if (data.drugs.length === 1) {
      // handle single drug
      transmissionData.compound_code = "1";
      transmissionData.ndc = data.drugs[0].ndc;
      transmissionData.quantity_dispensed = data.drugs[0].quantity;
    } else if (data.drugs.length > 1) {
      // handle compound
      transmissionData.product_service_id_qualifier = "00";
      transmissionData.compound_code = "2";
      transmissionData.ndc = "0";
      transmissionData.compound_ingredient_component_count = data.drugs.length;

      const compoundFields = {} as transmissionOptions;

      data.drugs.forEach((drug, index) => {
        const drugIndex = index + 1;
        compoundFields["compound_product_id_" + drugIndex] = drug.ndc;
        compoundFields["compound_product_id_qualifier_" + drugIndex] = "03";
        compoundFields["compound_ingredient_quantity_" + drugIndex] = drug.quantity;
      });

      transmissionData = { ...transmissionData, ...compoundFields };
    }

    return transmissionData;
  };

  const handleFormSubmit = (data: TransmissionTypes.TransmissionTestFormData): void => {
    const transmissionData = parseFormData(data);

    if (data.prescription_service_reference_number) {
      handleAmplitudeLogging(ANALYTIC_EVENTS.TEST_CLAIM.serviceReferenceNumberFilled);
    }
    handleAmplitudeLogging(ANALYTIC_EVENTS.TEST_CLAIM.testClaimAdjudicate);

    dispatch(submitTransmissionTest(transmissionData, "TestClaim"));
  };

  const selector = formValueSelector(formName);

  const checkFormValues = useSelector((state: RootState) => {
    return {
      days_supply: selector(state, "days_supply"),
      fill_number: selector(state, "fill_number"),
      daw: selector(state, "daw"),
      prescription_service_reference_number: selector(state, "prescription_service_reference_number"),
      date_of_service: selector(state, "date_of_service"),
      usual_and_customary_charge: selector(state, "usual_and_customary_charge"),
      submission_clarification_code_1: selector(state, "submission_clarification_code_1"),
      diagnosis_code_qualifier_1: selector(state, "diagnosis_code_qualifier_1"),
      diagnosis_code_1: selector(state, "diagnosis_code_1"),
      professional_service_code: selector(state, "professional_service_code"),
      dur_pps_level_of_effort: selector(state, "dur_pps_level_of_effort"),
      reason_for_service_code: selector(state, "reason_for_service_code"),
      result_of_service_code: selector(state, "result_of_service_code"),
      prescriber_id_qualifier: selector(state, "prescriber_id_qualifier"),
      prescriber_id: selector(state, "prescriber_id"),
      pharmacy_service_type: selector(state, "pharmacy_service_type"),
      place_of_service: selector(state, "place_of_service"),
      patient_residence: selector(state, "patient_residence"),
      prescription_origin_code: selector(state, "prescription_origin_code"),

      ingredient_cost_submitted: selector(state, "ingredient_cost_submitted"),
      dispensing_fee_submitted: selector(state, "dispensing_fee_submitted"),
      incentive_amount_submitted: selector(state, "incentive_amount_submitted"),

      other_coverage_code: selector(state, "other_coverage_code"),
      other_payer_id_1: selector(state, "other_payer_id_1"),
      other_payer_id_qualifier_1: selector(state, "other_payer_id_qualifier_1"),
      other_payer_amount_paid_1: selector(state, "other_payer_amount_paid_1"),
      other_payer_amount_paid_qualifier_1: "07",
      other_payer_patient_responsibility_amount_1: selector(state, "other_payer_patient_responsibility_amount_1"),
      other_payer_patient_responsibility_amount_qualifier_1: "06", // XXX: should take from claim
      other_payer_reject_code_1: selector(state, "other_payer_reject_code_1"),

      special_packaging_indicator: selector(state, "special_packaging_indicator"),
    };
  });

  const testAsBatchValues = useSelector((state: RootState) => {
    return {
      ...checkFormValues,
      details: selector(state, "details"),
      plan: selector(state, "plan"),
      plan_account: selector(state, "plan_account"),
      pharmacy: selector(state, "pharmacy"),
      drugs: selector(state, "drugs"),
      group: selector(state, "group"),
    };
  });

  const handleTestAsBatch = (): void => {
    const formData: TransmissionTypes.TransmissionTestFormData = testAsBatchValues;
    const transmissionData = parseFormData(formData);

    dispatch(submitTransmissionTestAsBatch(transmissionData, "TestClaim"));
  };

  const handleOnBlur = (): void => {
    const queryParams = queryString.getQueryParams(search);

    if (checkFormValues.date_of_service) {
      queryParams.date_of_service = checkFormValues.date_of_service;
    } else {
      delete checkFormValues["date_of_service"];
    }

    if (checkFormValues.prescription_service_reference_number) {
      queryParams.prescription_service_reference_number = checkFormValues.prescription_service_reference_number;
    } else {
      delete checkFormValues["prescription_service_reference_number"];
    }

    if (checkFormValues.days_supply) {
      queryParams.days_supply = checkFormValues.days_supply;
    } else {
      delete checkFormValues["days_supply"];
    }

    if (checkFormValues.fill_number) {
      queryParams.fill_number = checkFormValues.fill_number;
    } else {
      delete checkFormValues["fill_number"];
    }

    if (checkFormValues.daw) {
      queryParams.daw = checkFormValues.daw;
    } else {
      delete checkFormValues["daw"];
    }

    if (checkFormValues.usual_and_customary_charge) {
      queryParams.usual_and_customary_charge = checkFormValues.usual_and_customary_charge;
    } else {
      delete checkFormValues["usual_and_customary_charge"];
    }

    if (checkFormValues.submission_clarification_code_1) {
      queryParams.submission_clarification_code_1 = checkFormValues.submission_clarification_code_1;
    } else {
      delete checkFormValues["submission_clarification_code_1"];
    }

    if (checkFormValues.diagnosis_code_qualifier_1) {
      queryParams.diagnosis_code_qualifier_1 = checkFormValues.diagnosis_code_qualifier_1;
    } else {
      delete checkFormValues["diagnosis_code_qualifier_1"];
    }

    if (checkFormValues.diagnosis_code_1) {
      queryParams.diagnosis_code_1 = checkFormValues.diagnosis_code_1;
    } else {
      delete checkFormValues["diagnosis_code_1"];
    }

    if (checkFormValues.patient_residence) {
      queryParams.patient_residence = checkFormValues.patient_residence;
    } else {
      delete checkFormValues["patient_residence"];
    }

    if (checkFormValues.place_of_service) {
      queryParams.place_of_service = checkFormValues.place_of_service;
    } else {
      delete checkFormValues["place_of_service"];
    }

    if (checkFormValues.pharmacy_service_type) {
      queryParams.pharmacy_service_type = checkFormValues.pharmacy_service_type;
    } else {
      delete checkFormValues["pharmacy_service_type"];
    }

    if (checkFormValues.prescriber_id) {
      queryParams.prescriber_id = checkFormValues.prescriber_id;
    } else {
      delete checkFormValues["prescriber_id"];
    }

    if (checkFormValues.prescriber_id_qualifier) {
      queryParams.prescriber_id_qualifier = checkFormValues.prescriber_id_qualifier;
    } else {
      delete checkFormValues["prescriber_id_qualifier"];
    }

    if (checkFormValues.result_of_service_code) {
      queryParams.result_of_service_code = checkFormValues.result_of_service_code;
    } else {
      delete checkFormValues["result_of_service_code"];
    }

    if (checkFormValues.reason_for_service_code) {
      queryParams.reason_for_service_code = checkFormValues.reason_for_service_code;
    } else {
      delete checkFormValues["reason_for_service_code"];
    }

    if (checkFormValues.dur_pps_level_of_effort) {
      queryParams.dur_pps_level_of_effort = checkFormValues.dur_pps_level_of_effort;
    } else {
      delete checkFormValues["dur_pps_level_of_effort"];
    }

    if (checkFormValues.professional_service_code) {
      queryParams.professional_service_code = checkFormValues.professional_service_code;
    } else {
      delete checkFormValues["professional_service_code"];
    }

    if (checkFormValues.prescription_origin_code) {
      queryParams.prescription_origin_code = checkFormValues.prescription_origin_code;
    } else {
      delete checkFormValues["prescription_origin_code"];
    }

    if (checkFormValues.ingredient_cost_submitted || queryParams.ingredient_cost_submitted) {
      queryParams.ingredient_cost_submitted = checkFormValues.ingredient_cost_submitted;
    } else {
      delete checkFormValues["ingredient_cost_submitted"];
    }

    if (checkFormValues.dispensing_fee_submitted || queryParams.dispensing_fee_submitted) {
      queryParams.dispensing_fee_submitted = checkFormValues.dispensing_fee_submitted;
    } else {
      delete checkFormValues["dispensing_fee_submitted"];
    }

    if (checkFormValues.incentive_amount_submitted || queryParams.incentive_amount_submitted) {
      queryParams.incentive_amount_submitted = checkFormValues.incentive_amount_submitted;
    } else {
      delete checkFormValues["incentive_amount_submitted"];
    }

    if (checkFormValues.other_coverage_code || queryParams.other_coverage_code) {
      queryParams.other_coverage_code = checkFormValues.other_coverage_code;
    } else {
      delete checkFormValues["other_coverage_code"];
    }

    if (checkFormValues.other_payer_id_qualifier_1 || queryParams.other_payer_id_qualifier_1) {
      queryParams.other_payer_id_qualifier_1 = checkFormValues.other_payer_id_qualifier_1;
    } else {
      delete checkFormValues["other_payer_id_qualifier_1"];
    }

    if (checkFormValues.other_payer_id_1 || queryParams.other_payer_id_1) {
      queryParams.other_payer_id_1 = checkFormValues.other_payer_id_1;
    } else {
      delete checkFormValues["other_payer_id_1"];
    }

    if (checkFormValues.other_payer_amount_paid_1 || queryParams.other_payer_amount_paid_1) {
      queryParams.other_payer_amount_paid_1 = checkFormValues.other_payer_amount_paid_1;
    } else {
      delete checkFormValues["other_payer_amount_paid_1"];
    }

    if (checkFormValues.other_payer_patient_responsibility_amount_1 || queryParams.other_payer_patient_responsibility_amount_1) {
      queryParams.other_payer_patient_responsibility_amount_1 = checkFormValues.other_payer_patient_responsibility_amount_1;
    } else {
      delete checkFormValues["other_payer_patient_responsibility_amount_1"];
    }

    if (checkFormValues.other_payer_reject_code_1 || queryParams.other_payer_reject_code_1) {
      queryParams.other_payer_reject_code_1 = checkFormValues.other_payer_reject_code_1;
    } else {
      delete checkFormValues["other_payer_reject_code_1"];
    }

    if (checkFormValues.special_packaging_indicator || queryParams.special_packaging_indicator) {
      queryParams.special_packaging_indicator = checkFormValues.special_packaging_indicator;
    } else {
      delete checkFormValues["special_packaging_indicator"];
    }

    navigate(`/test-claim?${queryString.convertJsonToQueryString(queryParams)}`);
  };


  const patientSummary = member_id ? (
    <PatientSummary
      sectionTitle="Search for and add a patient to the test claim."
      path={"/test-claim/patient" + search}
      buttonText="Add Patient"
      data-testid="test__add_patient"
      handleClick={() => handleAmplitudeLogging(ANALYTIC_EVENTS.TEST_CLAIM_PATIENT.removePatientFromTestClaim)}
    />
  ) : (
    <TestClaimImageContainer
      sectionTitle="Search for and add a patient to the test claim."
      sectionImage={<PatientSearchImage />}
      buttonText="Add Patient"
      path={"/test-claim/patient" + search}
      handleClick={() => handleAmplitudeLogging(ANALYTIC_EVENTS.TEST_CLAIM_PATIENT.addPatientToTestClaim)}
    />
  );

  const pharmacySummary = npi ? (
    <PharmacySummary
      sectionTitle="Search for and add a pharmacy to the test claim."
      path={"/test-claim/pharmacy" + search}
      buttonText="Add Pharmacy"
      handleClick={() => handleAmplitudeLogging(ANALYTIC_EVENTS.TEST_CLAIM_PHARMACY.removePharmacyFromTestClaim)}
    />
  ) : (
    <TestClaimImageContainer
      sectionTitle="Search for and add a pharmacy to the test claim."
      sectionImage={<PharmacySearchImage />}
      buttonText="Add Pharmacy"
      path={"/test-claim/pharmacy" + search}
      handleClick={() => handleAmplitudeLogging(ANALYTIC_EVENTS.TEST_CLAIM_PHARMACY.addPharmacyToTestClaim)}
    />
  );

  const drugTitle = Array.isArray(drugs) ? "Compound Drug" : "Single Drug";
  const drugSummary = drugs ? (
    <DrugSummary sectionTitle={drugTitle} path={"/test-claim/drug" + search} buttonText="Add Drug" handleFieldOnBlur={handleOnBlur} />
  ) : (
    <TestClaimImageContainer
      sectionTitle="Search for and add drug(s) to the test claim."
      sectionImage={<DrugSearchImage />}
      buttonText="Add Drug"
      path={"/test-claim/drug" + search}
      handleClick={() => handleAmplitudeLogging(ANALYTIC_EVENTS.TEST_CLAIM_DRUG.addDrugFromTestClaim)}
    />
  );

  const testAsBatchButton = canProcessTestAsBatch ? (
    <Button
      disabled={member_id && npi && drugs && date_of_service && !submitting ? false : true}
      type="button"
      extraClasses="capitalrx-submitbtn ml20"
      color="lightblue"
      handleClick={handleTestAsBatch}
    >
      <span className="caprx-icon-link" />
      Process as Batch
    </Button>
  ) : (
    <></>
  );



  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <TestClaimSectionContainer readOnlyTheme={false} title="GENERAL INFORMATION">
        <GeneralClaimInformation handleFieldOnBlur={handleOnBlur} />
      </TestClaimSectionContainer>
      <TestClaimSectionContainer title="PATIENT">{patientSummary}</TestClaimSectionContainer>
      <TestClaimSectionContainer title="PHARMACY">{pharmacySummary}</TestClaimSectionContainer>
      <TestClaimSectionContainer title="DRUG">{drugSummary}</TestClaimSectionContainer>
      <div className="row caprx-h-center-text">
        <SubmitBtn fullWidth={false} submitting={submitting} disabled={member_id && npi && drugs && date_of_service ? false : true}>
          Test Adjudication
        </SubmitBtn>
        {testAsBatchButton}
      </div>
    </form>
  );
};

export default reduxForm({ form: formName, enableReinitialize: true })(TestClaimForm);
